import { useState } from "react";
import type { Layout, LayoutEntity, LayoutSection } from "../lib/types";
import { Icon } from "./icons";
import { WIDGET_DEFS } from "./widgets";

/* The layout builder. Toggle widgets on/off, reorder them, and set each to
 * full- or half-width — the Gainsight-style "design your own 360" control.
 * Works on a local copy and hands the new section list back on save. */

export function LayoutEditor({ layout, entity, onSave, onClose }: {
  layout: Layout; entity: LayoutEntity; onSave: (sections: LayoutSection[]) => void; onClose: () => void;
}) {
  const valid = (w: string) => WIDGET_DEFS[w]?.entities.includes(entity);
  const [secs, setSecs] = useState<LayoutSection[]>(layout.sections.filter((s) => valid(s.widget)));

  const inLayout = new Set(secs.map((s) => s.widget));
  const addable = Object.keys(WIDGET_DEFS).filter((w) => valid(w) && !inLayout.has(w));

  const move = (i: number, d: -1 | 1) => setSecs((prev) => {
    const next = [...prev]; const j = i + d;
    if (j < 0 || j >= next.length) return prev;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const patch = (id: string, p: Partial<LayoutSection>) => setSecs((prev) => prev.map((s) => (s.id === id ? { ...s, ...p } : s)));
  const remove = (id: string) => setSecs((prev) => prev.filter((s) => s.id !== id));
  const add = (widget: string) => setSecs((prev) => [...prev, { id: `s_${widget}_${prev.length}`, widget, span: "full", enabled: true }]);

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="compose-modal__hd">
          <div className="row" style={{ gap: 8 }}><Icon name="grid" size={16} /><b>Customize {layout.name}</b></div>
          <button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button>
        </div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 8, maxHeight: "60vh", overflowY: "auto" }}>
          {secs.map((s, i) => (
            <div key={s.id} className={`le-row ${s.enabled ? "" : "le-row--off"}`}>
              <span className="le-grip"><Icon name="dot" size={14} /></span>
              <div className="grow"><b style={{ fontSize: 13 }}>{WIDGET_DEFS[s.widget]?.label ?? s.widget}</b></div>
              <div className="row" style={{ gap: 4 }}>
                <button className="le-move" disabled={i === 0} onClick={() => move(i, -1)} title="Move up"><Icon name="arrowUp" size={12} /></button>
                <button className="le-move" disabled={i === secs.length - 1} onClick={() => move(i, 1)} title="Move down"><Icon name="arrowDown" size={12} /></button>
              </div>
              <select className="inp inp--sm" value={s.span} onChange={(e) => patch(s.id, { span: e.target.value as "full" | "half" })}>
                <option value="full">Full</option><option value="half">Half</option>
              </select>
              <label className="toggle" title={s.enabled ? "Shown" : "Hidden"}><input type="checkbox" checked={s.enabled} onChange={() => patch(s.id, { enabled: !s.enabled })} /><span /></label>
              <button className="icon-btn" onClick={() => remove(s.id)} title="Remove"><Icon name="dot" size={14} /></button>
            </div>
          ))}
          {addable.length > 0 && (
            <div className="row" style={{ gap: 8, marginTop: 6, alignItems: "center" }}>
              <span className="labeled__l">Add widget</span>
              <select className="inp inp--sm" value="" onChange={(e) => { if (e.target.value) add(e.target.value); }}>
                <option value="">Choose…</option>
                {addable.map((w) => <option key={w} value={w}>{WIDGET_DEFS[w].label}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="compose-modal__ft">
          <button className="btn btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={() => onSave(secs)}>Save layout</button>
        </div>
      </div>
    </div>
  );
}
